import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "@/store";
import {
  createScraper,
  updateScraper,
  fetchStats,
} from "@/store/scrapersSlice";
import { createCronJob } from "@/store/cronJobsSlice";
import * as scrapersApi from "@api/scrapers.api";
import { useNotification } from "@context/NotificationContext";
import { extractErrorMessage } from "@api/axios";
import type { Scraper } from "@/types/index";
import {
  HttpMethod,
  LinkExtractorType,
  SubmitConfigType,
} from "@/constants/scraper";
import {
  emptyExtractor,
  emptyTransformer,
  transformerFromApi,
  transformerToApi,
  type ExtractorFormState,
  type TransformerFormState,
} from "@/scraper-form/transformerState";

export function useScraperForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { notify } = useNotification();

  const [name, setName] = useState("");
  const [base, setBase] = useState("");
  const [favicon, setFavicon] = useState("");
  const [roots, setRoots] = useState<string[]>([""]);
  const [linkMethod, setLinkMethod] = useState<string>(HttpMethod.GET);
  const [linkType, setLinkType] = useState<string>(LinkExtractorType.Html);
  const [linkSelector, setLinkSelector] = useState("");
  const [removeSelectors, setRemoveSelectors] = useState<string[]>([""]);
  const [extractors, setExtractors] = useState<ExtractorFormState[]>([
    emptyExtractor(),
  ]);
  const [submitType, setSubmitType] = useState<string>(SubmitConfigType.Store);
  const [submitUrl, setSubmitUrl] = useState("");
  const [submitMethod, setSubmitMethod] = useState<string>(HttpMethod.POST);
  const [cronExpression, setCronExpression] = useState("");
  const [cronName, setCronName] = useState("");

  const patchExtractor = useCallback(
    (i: number, patch: Partial<ExtractorFormState>) => {
      setExtractors((prev) => {
        const next = [...prev];
        next[i] = { ...next[i], ...patch };
        return next;
      });
    },
    [],
  );

  const patchTransformer = useCallback(
    (
      extIndex: number,
      trIndex: number,
      patch: Partial<TransformerFormState>,
    ) => {
      setExtractors((prev) => {
        const next = [...prev];
        const trs = [...next[extIndex].transformers];
        trs[trIndex] = { ...trs[trIndex], ...patch };
        next[extIndex] = { ...next[extIndex], transformers: trs };
        return next;
      });
    },
    [],
  );

  useEffect(() => {
    if (!isEdit || !id) return;

    scrapersApi.getOne(id).then((scraper) => {
      setName(scraper.name);
      setBase(scraper.base);
      setFavicon(scraper.favicon || "");
      setRoots(scraper.roots.length > 0 ? scraper.roots : [""]);
      setLinkMethod(scraper.links.fetching.method);
      setLinkType(scraper.links.type);
      setLinkSelector(scraper.links.selector);
      setRemoveSelectors(
        scraper.remove && scraper.remove.length > 0 ? scraper.remove : [""],
      );
      setExtractors(
        scraper.scrape.map((e) => ({
          property: e.property,
          selector: e.selector,
          type: e.type || "text",
          take: e.take || "normal",
          transformers: (e.transformers || []).map(transformerFromApi),
          remove: e.remove?.length ? [...e.remove] : [],
        })),
      );
      if (scraper.submit) {
        setSubmitType(scraper.submit.type);
        setSubmitUrl(scraper.submit.url || "");
        setSubmitMethod(scraper.submit.method || HttpMethod.POST);
      }
    });
  }, [id, isEdit]);

  const handleSubmit = async () => {
    const data = {
      name,
      base,
      favicon: favicon || undefined,
      roots: roots.filter(Boolean),
      links: {
        fetching: { method: linkMethod },
        type: linkType,
        selector: linkSelector,
      },
      remove: removeSelectors.filter(Boolean),
      scrape: extractors
        .filter((e) => e.property && e.selector)
        .map((e) => {
          const row: {
            property: string;
            selector: string;
            type: string;
            take: string;
            transformers?: Record<string, unknown>[];
            remove?: string[];
          } = {
            property: e.property,
            selector: e.selector,
            type: e.type,
            take: e.take,
          };
          if (e.transformers.length > 0) {
            row.transformers = e.transformers.map(transformerToApi);
          }
          const rem = e.remove.filter(Boolean);
          if (rem.length > 0) row.remove = rem;
          return row;
        }),
      submit:
        submitType === SubmitConfigType.Request && submitUrl
          ? { type: submitType, url: submitUrl, method: submitMethod }
          : { type: SubmitConfigType.Store as const },
    } as Partial<Scraper>;

    try {
      if (isEdit && id) {
        await dispatch(updateScraper({ id, data })).unwrap();
        notify(t("scrapers.scraperUpdated"), "success");
      } else {
        const created = await dispatch(createScraper(data)).unwrap();
        dispatch(fetchStats());
        if (cronExpression && cronName) {
          await dispatch(
            createCronJob({
              scraperId: created.id,
              name: cronName,
              expression: cronExpression,
            }),
          ).unwrap();
        }
        notify(t("scrapers.scraperCreated"), "success");
      }
      navigate("/scrapers");
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
  };

  return {
    t,
    isEdit,
    navigate,
    name,
    setName,
    base,
    setBase,
    favicon,
    setFavicon,
    roots,
    setRoots,
    linkMethod,
    setLinkMethod,
    linkType,
    setLinkType,
    linkSelector,
    setLinkSelector,
    removeSelectors,
    setRemoveSelectors,
    extractors,
    setExtractors,
    submitType,
    setSubmitType,
    submitUrl,
    setSubmitUrl,
    submitMethod,
    setSubmitMethod,
    cronExpression,
    setCronExpression,
    cronName,
    setCronName,
    patchExtractor,
    patchTransformer,
    handleSubmit,
    emptyTransformer,
    emptyExtractor,
  };
}
